import * as Sentry from "@sentry/react";

import {
  getCollectionOfDittoComponents,
  useTexts,
} from "Components/TextContext";
import {
  BackendProductInfo,
  MAX_AGE_AKA_STUDENT_INSURANCE_CUSTOMER,
  MIN_AGE_AKA_STUDENT_INSURANCE_CUSTOMER,
} from "Features/CheckPrice/Products/productConstants";
import { Form, FormikProps } from "formik";
import { useDispatch, useSelector } from "react-redux";
import {
  AkaStudentProductIntroFormValues,
  CountryCode,
} from "./akaStudentProductIntroInterfaces";
import {
  filterRegionError,
  findCountry,
  mapToCountries,
} from "./akaStudentProductIntroUtils";

import { Icons } from "@storebrand-digital/insurance-definitions";
import { MarketingConsent } from "Components/Consent/MarketingConsent";
import { CheckPriceProps } from "Components/Features/CheckPrice";
import { ProductIntroTile } from "Components/Features/CheckPrice/Tiles/ProductIntroTile";
import { PartnerProductIntroForms } from "Components/Features/CheckPrice/Tiles/ProductIntroTile/PartnerProductIntroForms/PartnerProductIntroForms";
import { AlertWithIllustration } from "Components/Form/Alert";
import { SystemErrorAlert } from "Components/Form/Alert/SystemErrorAlert";
import { ErrorsSummedUp } from "Components/Form/ErrorsSummedUp";
import { InputFailureTracker } from "Components/InputFailureTracker";
import { NavigationButtons } from "Components/NavigationButtons";
import { ScrollToFirstError } from "Components/ScrollToFirstError";
import { TextComponentNameLevel1 } from "Components/TextContext/textConstants";
import { IllustrationName } from "Constants/constants";
import { ProductBackendName } from "Features/CheckPrice/Products/productTypes";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { useFormState } from "Store/priceCalculation/priceCalculationHooks";
import { usePriceCalculationSelector } from "Store/priceCalculation/priceCalculationSelectors";
import { updatePriceCalculationFormState } from "Store/priceCalculation/priceCalculationSlice";
import { useAkaStudentCountries } from "Store/texts/hooks";
import { customerDateOfBirthSelector } from "Store/userIdentification/userIdentificationSelectors";
import { mapValidationErrorsDitto } from "Utils/formUtils";
import React from "react";
import { customerIsWithinAgeRange } from "../../../RiskProducts/riskProductsUtils";
import { AkaStudentPriceRequestInfo } from "../akaStudentInterfaces";
import { AKA_STUDENT_ABOUT_YOU_STEP } from "../akaStudentSteps";
import { AkaStudentProductIntroFormElements } from "./AkaStudentProductIntroFormElements";
import { AKA_STUDENT_PRODUCT_INTRO_INITIAL_FORM_VALUES } from "./akaStudentProductIntroConstants";
import { validationSchema } from "./akaStudentProductIntroValidation";

export const AkaStudentProductIntro = ({
  updatePriceRequest,
}: CheckPriceProps): React.ReactElement => {
  const dispatch = useDispatch();
  const { getText } = useTexts();
  const { priceCalculationResult } = usePriceCalculationSelector();
  const initialFormState = useFormState<AkaStudentProductIntroFormValues>(
    AKA_STUDENT_ABOUT_YOU_STEP.id,
    AKA_STUDENT_PRODUCT_INTRO_INITIAL_FORM_VALUES,
  );

  const customerDateOfBirth = useSelector(customerDateOfBirthSelector);
  const isCustomerWithinAgeRange = customerIsWithinAgeRange(
    customerDateOfBirth,
    MAX_AGE_AKA_STUDENT_INSURANCE_CUSTOMER,
    MIN_AGE_AKA_STUDENT_INSURANCE_CUSTOMER,
  );

  const { optionsList, countriesNotAllowed } = useAkaStudentCountries();
  const countries = mapToCountries(optionsList);
  const { shorttitle: shortTitleComponents } = getCollectionOfDittoComponents(
    TextComponentNameLevel1.AKA_STUDENT,
  );

  const handleSubmit = (form: AkaStudentProductIntroFormValues): void => {
    const country = findCountry(form.country, countries);
    const region = country?.code ?? form.region;

    dispatch(
      updatePriceCalculationFormState({
        stepId: AKA_STUDENT_ABOUT_YOU_STEP.id,
        formState: {
          ...form,
          region,
        },
      }),
    );

    updatePriceRequest<Omit<AkaStudentPriceRequestInfo, "dateOfBirth">>({
      productPriceUrl: BackendProductInfo.AKA_STUDENT.priceReqUrl,
      stepForwardOnSuccess: true,
      updatedProductPriceRequestInfo: {
        studiesAbroad: region !== CountryCode.NO,
        region,
      },
    });
  };

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag(
          "location",
          `CheckPrice - ProductIntro - ${INSURANCE_NAME_VALUE.AKA_STUDENT}`,
        );
      }}
    >
      <ProductIntroTile
        illustrationName={IllustrationName.ESTABLISHMENT_FAMILY}
        productBackendName={ProductBackendName.AKA_STUDENT}
        validationSchema={validationSchema(countries, countriesNotAllowed)}
        initialValues={initialFormState}
        onSubmit={handleSubmit}
      >
        {(formProps: FormikProps<AkaStudentProductIntroFormValues>) => {
          return (
            <Form>
              {!isCustomerWithinAgeRange ? (
                <AlertWithIllustration
                  title={getText(
                    "akastudentform.alerttitle.customernotwithinagerange",
                  )}
                  iconName={Icons.TRAVEL}
                  marginBottom="32px"
                />
              ) : (
                <>
                  <AkaStudentProductIntroFormElements />
                  <PartnerProductIntroForms />
                  <MarketingConsent />
                  {priceCalculationResult.error && (
                    <SystemErrorAlert alertId="priceCalculation" />
                  )}
                  <ScrollToFirstError />
                  <ErrorsSummedUp
                    errors={mapValidationErrorsDitto(
                      filterRegionError(formProps.errors),
                      shortTitleComponents,
                    )}
                    isVisible={false}
                  />
                </>
              )}

              <NavigationButtons
                hidePreviousButton
                isLoading={priceCalculationResult.isLoading}
                disableNextButton={!isCustomerWithinAgeRange}
              />
              <InputFailureTracker
                productName={INSURANCE_NAME_VALUE.AKA_STUDENT}
                stepId={AKA_STUDENT_ABOUT_YOU_STEP.id}
              />
            </Form>
          );
        }}
      </ProductIntroTile>
    </Sentry.ErrorBoundary>
  );
};
